import MatchesOddsModel from "./match-odds-model";
import MatchesOutcomeModel from "./match-outcome-model";
import { LAYOUT_NAME } from '../../../../models/enums/layout-const';
import MarketInfoModel from 'bto-models/match/live/market-info.model';

/**
 * Live match model
 */
export default class LiveMatchModel {
  /**
   * Class constructor method
   */
  constructor(data, APP_SETTINGS, betItems = {}, selectedBetItems = []) {
    /**
     * create refference to this data so we can use it for cloning
     * @type {object}
     */
    this.data = data;
    /**
     * Sport name
     * @type {string}
     */
    this.sportName = data.lsn;
    /**
     * Home team name
     * @type {string}
     */
    this.homeTeam = data.ht;
    /**
     * Home team Icon, we get this info from betradar
     * @type {string}
     */
    this.homeTeamIcon = data.hti ? `${APP_SETTINGS.path.teamImageUrl}${data.hti}` : '';
    /**
     * Guest team name
     * @type {string}
     */
    this.guestTeam = data.at;
    /**
     * Guest team Icon, we get this info from betradar
     * @type {string}
     */
    this.guestTeamIcon = data.ati ? `${APP_SETTINGS.path.teamImageUrl}${data.ati}` : '';
    /**
     * Sport ID
     * @type {number}
     */
    this.sportId = data.sid;
    /**
     * Sport -> Category ID
     * @type {number}
     */
    this.categoryId = data.cid;
    /**
     * Sport -> Category Name
     * @type {string}
     */
    this.categoryName = data.lcn;
    /**
     * Sport -> Category -> Tournament ID
     * @type {number}
     */
    this.tournamentId = data.tid;
    /**
     * Sport -> Category -> Tournament Name
     * @type {string}
     */
    this.tournamentName = data.ltn;
    /**
     * Sport -> Category -> Tournament -> Match ID
     * @type {number}
     */
    this.matchId = data.mid;
    /**
     * Match ID from betradar
     * @type {number}
     */
    this.betRadarId = data.brid;

    /**
     * Match time
     * @type {string}
     */
    this.time = data.mt;
    /**
     * Match Odds + Odds->Outcomes
     * @type {array}
     */
    this.odds = this.mapOdds(data, betItems, selectedBetItems, APP_SETTINGS.settings.layout);
    /**
     * Match number of odds
     * @type {number}
     */
    this.numberOfOdds = data.nro;
    /**
     * Number of markets
     * @type {number}
     */
    this.numberOfMarkets = data.nrm;
    /**
     * Bet Stop
     * @type {boolean}
     */
    this.isBetStop = data.ibs;
    /**
     * Match status
     * @type {string}
     */
    this.status = data.ms;
    /**
     * Sport order
     * @type {number}
     */
    this.sportOrder = data.sor;
    /**
     * Tournament order
     * @type {number}
     */
    this.tournamentOrder = data.tor;
    /**
     * Match order
     * @type {number}
     */
    this.matchOrder = data.mor;
    /**
     * Score
     */
    this.setScore(data.sc);
    /**
     * Points
     */
    this.setPoints(data.gs);
    /**
     * Games
     */
    this.setGames(data.ss);
    /**
     * Live match indicator
     */
    this.isLiveMatch = true;
    /**
     * Event code
     * @type {number}
     */
    this.eventCode = data.ec;
    /**
     * Feed provider code
     * @type {string}
     */
    this.feedProvider = data.fpc;
    /**
     * Suspended match
     * @type {boolean}
     */
    this.suspended = data.su;
    /**
     * List of live markets info
     * @type {MarketInfoModel[]}
     */
    this.marketsInfo = (data.lmo || []).map(o => new MarketInfoModel(o));
    /**
     * is tournament US
     * @type {boolean}
     */
    this.awayAtHome = data.tus;
    /**
     * Flag if  match has live stream
     * @type {boolean}
     */
    this.hasLiveStream = data.lc;

    this.name = this.getName(data);
    /**
     * cash out visibility
     * @type {boolean}
     */
    this.cashOutVisibility = data.cs;
    /**
     * Number of scores to display
     * @type {number}
     */
    this.scoresToDisplay = data.lst;

    /**
     * All match scores
     * @type {array}
     */
    this.allScores = this.getAllScores();
  }

  getName(data) {
    // TODO: add translations
    if (data.tus) {
      return `${this.guestTeam} @ ${this.homeTeam}`;
    }
    return `${this.homeTeam} vs ${this.guestTeam}`;
  }
  /**
   * Modify Score to be object
   * @param score
   */
  setScore(score) {
    this.score = {
      home: '--',
      away: '--',
    };
    if (score) {
      let matchScore = score.split(':');
      this.score.home = matchScore[0] || '--';
      this.score.away = matchScore[1] || '--';
    }
  }
  /**
   * Modify Points to be object
   * @param points
   */
  setPoints(points) {
    if (!points) {
      this.points = {
        home: '-',
        away: '-',
      };
      return;
    }
    let matchPoints = points.split(':');
    this.points = {
      home: matchPoints[0] || '-',
      away: matchPoints[1] || '-',
    };
  }

  /**
   * Modify Games to be object
   * @param {string} setsScore
   */
  setGames(setsScore) {
    if (!setsScore) {
      this.games = {
        home: '-',
        away: '-',
      };
      return;
    }
    let matchSets = setsScore.split('|');
    let currentSet = matchSets[matchSets.length - 1].split(':');
    this.games = {
      home: currentSet[0] || '-',
      away: currentSet[1] || '-',
    };
  }
  /**
   * Combine all the match scores
   * @return arr of all the match scores
   */
  getAllScores() {
    const allScores = [];
    allScores.push(this.score);
    allScores.push(this.games);
    allScores.push(this.points);
    return allScores.slice(0, this.scoresToDisplay);
  }

  /**
   * Method for mapping inner objects from API with parent model
   * @param oddsList
   * @returns {[]|*[]}
   */
  mapOdds(data, betItems = {}, selectedBetItems = [], layout) {
    let oddsList = data.odds;
    if (!oddsList) {
      return {};
    }
    let odds = {};
    // sort odds by market Id
    oddsList.sort((m1, m2) => {
      return m1.id > m2.id ? 1 : -1;
    });
    oddsList.forEach((o) => {
      let odd = new MatchesOddsModel(o, data);
      o.m.forEach(m => {
        const outcome = new MatchesOutcomeModel(m, data);
        odd.outcomes.push(outcome);
        // Odds change logic
        const betItemKey = `${data.mid}_${odd.marketId}_${outcome.id}`;
        if (betItems[betItemKey]) {
          const oddChanged = (+outcome.outcome) - (+betItems[betItemKey].odd) !== 0;
          const isLocked = data.ibs || data.su || outcome.suspended || !outcome.active;
          if (isLocked || oddChanged) {
            selectedBetItems.push({ isBetStop: isLocked, newOdd: outcome.outcome, item: betItems[betItemKey] });
          }
        }
      });
      // sort outcomes only for default and latam layout
      if (layout !== LAYOUT_NAME.US) {
        odd.outcomes.sort((o1, o2) => o1.orderNo - o2.orderNo);
        odd.outcomes.sort((o1, o2) => o1.column - o2.column);
      }
      odds[odd.marketId] = odd;
    });
    return odds;
  }
  /**
   * Map live markets
   * @param {{[id:string]: string}} markets - Map of market Id -> market name
   */
  mapMarkets(markets) {
    if (!markets) {
      return [];
    }
    return Object.entries(markets).map(([id, name]) => ({ name, id }));
  }
  /**
   * Create new copy of this model with same data
   * this way we are creating deep copy of this object
   * @returns {LiveMatchModel}
   */
  clone() {
    return new LiveMatchModel(this.data);
  }
}
